import type { NotificationKind, NotificationView } from '@/types';

/** Minimal translate signature so this helper stays UI-library agnostic. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface NotificationMeta {
  icon: string;
  /** Token-based tone for the icon bubble (never colour-only meaning). */
  tone: string;
}

const META: Record<NotificationKind, NotificationMeta> = {
  new_order: { icon: 'ri-file-add-line', tone: 'bg-amber-50 text-amber-600' },
  order_handoff: { icon: 'ri-inbox-archive-line', tone: 'bg-secondary-100 text-secondary-600' },
  manual_assignment: { icon: 'ri-user-follow-line', tone: 'bg-amber-100 text-amber-700' },
  new_review: { icon: 'ri-star-line', tone: 'bg-accent-100 text-accent-600' },
  package_subscription: { icon: 'ri-vip-crown-line', tone: 'bg-primary-100 text-primary-700' },
};

export function notificationMeta(kind: NotificationKind): NotificationMeta {
  return META[kind] ?? { icon: 'ri-notification-3-line', tone: 'bg-background-100 text-foreground-500' };
}

export function notificationTitle(view: NotificationView, t: Translate): string {
  return t(`notifications.type.${view.kind}.title`);
}

/**
 * Build the short description from the data actually available. Customer names,
 * ratings and package names are only shown when the system returned them.
 */
export function notificationBody(view: NotificationView, t: Translate): string {
  switch (view.kind) {
    case 'new_order':
      return view.orderId
        ? t('notifications.type.new_order.body', { orderId: view.orderId })
        : t('notifications.type.new_order.bodyGeneric');
    case 'order_handoff':
      return view.orderId
        ? t('notifications.type.order_handoff.body', { orderId: view.orderId })
        : t('notifications.type.order_handoff.bodyGeneric');
    case 'manual_assignment':
      return view.orderId
        ? t('notifications.type.manual_assignment.body', { orderId: view.orderId })
        : t('notifications.type.manual_assignment.bodyGeneric');
    case 'new_review': {
      const hasName = Boolean(view.customerName);
      const hasRating = view.rating !== null;
      if (hasName && hasRating) {
        return t('notifications.type.new_review.bodyBoth', {
          customerName: view.customerName,
          rating: view.rating,
        });
      }
      if (hasName) {
        return t('notifications.type.new_review.bodyName', { customerName: view.customerName });
      }
      if (hasRating) {
        return t('notifications.type.new_review.bodyRating', { rating: view.rating });
      }
      return t('notifications.type.new_review.bodyGeneric');
    }
    case 'package_subscription': {
      const hasName = Boolean(view.customerName);
      const hasPackage = Boolean(view.packageName);
      if (hasName && hasPackage) {
        return t('notifications.type.package_subscription.bodyBoth', {
          customerName: view.customerName,
          packageName: view.packageName,
        });
      }
      if (hasPackage) {
        return t('notifications.type.package_subscription.bodyPackage', {
          packageName: view.packageName,
        });
      }
      if (hasName) {
        return t('notifications.type.package_subscription.bodyName', {
          customerName: view.customerName,
        });
      }
      return t('notifications.type.package_subscription.bodyGeneric');
    }
    default:
      return '';
  }
}